import { useCurrentAccount, useGetBreeds, useGetStorePromos } from '@application/api';
import {
  BreedInterestController,
  PreloadController,
  SeoController,
  SubscribeToDogBreedButtonController,
} from '@application/components';
import {
  filterRegionOptions,
  generateItemsWithPromos,
  useModal,
  useNewsletter,
  usePaginationSearch,
  useRedirectToParentBreed,
  useRouter,
} from '@application/utils';
import type { CountryCode } from '@gql/graphql';
import { DogBreedImgType } from '@gql/graphql';
import type { BreedOverviewData } from '@presentation/components';
import { DOG_BREED_INTEREST_BANNER_ID } from '@presentation/components';
import type { LitterListPageFormValues } from '@presentation/pages';
import { LitterListPage } from '@presentation/pages';
import { FormikProvider, useFormik } from 'formik';
import hash from 'object-hash';
import type { FC } from 'react';
import { useMemo, useState } from 'react';
import { useGetBreed } from '../../../../api/breeds/useGetBreed';
import { useFormatLitters } from './api/useFormatLitters';
import { useGetLitters } from './api/useGetLitters';
import { useLitterWhereBuilder } from './api/useLitterWhereBuilder';
import { FORM_INITIAL_VALUES, PAGINATED_SIZE } from './constants';
import { useBreedersCategories } from './useBreedersCategories';
import { useBreederWhereBuilder } from './useBreedersCategories/api/useBreederWhereBuilder';
import { useSearch } from './useSearch';
import { useSeo } from './useSeo';

export const LitterListPageController: FC = () => {
  const { language, infoFromCurrentPath, countryCode, translate } = useRouter();
  const { handlePaginationChange, currentPage } = usePaginationSearch({
    scrollToTopOnChange: false,
  });
  const { setSearchFromValues, valuesFromSearch } = useSearch();

  const { currentAccount } = useCurrentAccount();

  // TC-1410 - if path param breed is a sub-breed, redirect to parent breed
  useRedirectToParentBreed();

  const breedsAmount = valuesFromSearch?.breedNames?.length ?? 0;
  const showBreederCategories = breedsAmount > 0 && breedsAmount <= 2;

  const form = useFormik<LitterListPageFormValues>({
    enableReinitialize: true,
    initialValues: valuesFromSearch,
    onSubmit: (values) => {
      // defer execution like in BreederListPageController
      setTimeout(() => {
        setSearchFromValues(values);
      }, 0);

      setTimeout(() => {
        littersRefetch({ where: litterWhereBuilder(values) });
        if (showBreederCategories) {
          breedersRefetch({ where: breederWhereBuilder(values) });
        }
      }, 0);
    },
  });

  const formValuesChanged = useMemo(() => {
    const hashInitialValues = hash(FORM_INITIAL_VALUES);
    const hashValues = hash(form.values);
    return hashValues !== hashInitialValues;
  }, [form.values]);

  const [size, setSize] = useState(PAGINATED_SIZE * (currentPage + 1));

  const { breedsLoading, breedOptionsBasenameKey } = useGetBreeds();

  const { seoDescription, seoTitle, pageTitle } = useSeo();

  const {
    littersRefetch,
    littersFetchMoreLoading,
    litters,
    littersFetchMore,
    littersLoading,
    littersCount,
  } = useGetLitters();

  const { litterWhereBuilder } = useLitterWhereBuilder();
  const { breederWhereBuilder } = useBreederWhereBuilder();
  const { formattedLitters, imagesToPreload: littersImagesToPreload } = useFormatLitters(litters);

  const { newsletterProps } = useNewsletter();

  const { breederCategories, breedersLoading, breedersRefetch } = useBreedersCategories({
    show: showBreederCategories,
  });

  const { breed } = useGetBreed(
    valuesFromSearch?.breedNames?.length === 1 ? valuesFromSearch?.breedNames[0] : undefined,
  );
  const breedTranslation = breed?.translations.find((tr) => tr.language === language);
  const breedImage = breed?.imgs.find((img) => img.type === DogBreedImgType.MAIN);
  const puppiesAvailable = litters.reduce(
    (acc, curr) => acc + (curr.malesLeftCount ?? 0) + (curr.femalesLeftCount ?? 0),
    0,
  );

  const breedOverview =
    breed && breedTranslation && breedImage?.small
      ? ({
          count:
            litters.length > 0 && puppiesAvailable > 0
              ? translate('litterList.availableText')
                  .replace('$littersCount', litters.length.toString())
                  .replace('$puppiesCount', puppiesAvailable.toString())
                  .replace('$breed', breedTranslation.name ?? '')
              : undefined,
          image: breedImage.small,
          title: breedTranslation?.name,
          description: breedTranslation?.description,
          details: [
            {
              label: translate('glossary.otherNames'),
              value: breedTranslation?.otherNames.length
                ? breedTranslation?.otherNames.join(', ')
                : undefined,
            },
            {
              label: translate('glossary.fciGroup'),
              value: breed.fciGroup
                ? translate(`enum.breedFciGroupType.${breed?.fciGroup}`)
                : undefined,
            },
            {
              label: translate('glossary.hair'),
              value: breed.hairType
                ? translate(`enum.hairType.${breed.hairType.replace('_', '')}`)
                : undefined,
            },
            {
              label: translate('glossary.head'),
              value: breed.headShape ? translate(`enum.headShape.${breed.headShape}`) : undefined,
            },
          ],
          subscribeBtn: (
            <SubscribeToDogBreedButtonController
              subscriptionEmail={currentAccount?.email ?? null}
              breedName={breed?.name}
              onFallbackClick={() => {
                document
                  .getElementById(DOG_BREED_INTEREST_BANNER_ID)
                  ?.scrollIntoView({ behavior: 'smooth' });
              }}
              hasADog={currentAccount?.hasADog}
            />
          ),
        } as BreedOverviewData)
      : undefined;

  const { modal: filtersModal, showFromClick: filtersShow } = useModal();

  const loading = littersLoading || breedsLoading || breedersLoading;

  const regionOptions = filterRegionOptions(
    translate,
    countryCode,
    infoFromCurrentPath.search.countries as CountryCode[],
  );

  const imagesToPreload = breedImage?.small ? [breedImage.small] : littersImagesToPreload;

  const { listingPromoItems } = useGetStorePromos('litters');
  const items = useMemo(() => {
    return generateItemsWithPromos(
      formattedLitters ?? [],
      listingPromoItems,
      PAGINATED_SIZE * (currentPage + 1),
    );
  }, [currentPage, formattedLitters, listingPromoItems]);

  return (
    <>
      <PreloadController images={imagesToPreload} />
      <SeoController
        title={seoTitle}
        description={seoDescription}
        thumbnail={breedImage?.medium ?? undefined}
      />
      <FormikProvider value={form}>
        <LitterListPage
          currentPage={currentPage}
          breedOverview={breedOverview}
          pageTitle={pageTitle}
          formOptions={{
            breeds: breedOptionsBasenameKey,
            regions: regionOptions,
            price: {
              limit: [0, 5000],
            },
          }}
          filtersModal={{
            modal: filtersModal,
            onShowClick: filtersShow,
            loading,
            canReset: formValuesChanged,
            onResetClick: () => {
              setSearchFromValues(FORM_INITIAL_VALUES);
              littersRefetch({ where: litterWhereBuilder(FORM_INITIAL_VALUES) });
              breedersRefetch({ where: breederWhereBuilder(FORM_INITIAL_VALUES) });
            },
          }}
          newsletter={newsletterProps}
          loading={loading}
          items={items.list}
          breederCategories={breederCategories}
          canLoadMore={size < littersCount}
          loadMoreText={`${Math.min(size, littersCount)} / ${littersCount}`}
          onLoadMoreClick={async () => {
            const newSize = size + PAGINATED_SIZE;
            setSize(newSize);
            await littersFetchMore({
              variables: {
                take: PAGINATED_SIZE,
                skip: currentPage * PAGINATED_SIZE,
              },
            });
            handlePaginationChange(currentPage + 1);
          }}
          loadMoreLoading={littersFetchMoreLoading}
          breedInteretBanner={
            form.values.breedNames && form.values.breedNames.length > 0 ? (
              <BreedInterestController preselectedBreedName={form.values.breedNames} />
            ) : undefined
          }
        />
      </FormikProvider>
    </>
  );
};
